import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { CircleSlash, History, Paperclip, PencilLine, Plus } from 'lucide-react';

import { api } from '@/lib/api';
import { INTERVALO_ATUALIZACAO_MS } from '@/lib/consultas';
import { formatarData } from '@/lib/utils';
import { useAuth } from '@/features/auth/contexto';
import { ROTULOS_PERFIL } from '@/features/auth/tipos';
import type { Estatisticas } from '@/features/solicitacoes/tipos';
import { podeRegistrarSolicitacao } from '@/features/solicitacoes/origem';
import {
  DEFINICOES_STATUS,
  ROTULOS_TIPO,
  STATUS,
  VALOR_STATUS_ABERTOS,
  type StatusSolicitacao,
} from '@/features/solicitacoes/status';
import { CabecalhoPagina } from '@/components/cabecalho-pagina';
import { CartaoIndicador } from '@/components/cartao-indicador';
import { IndicadorAtualizacao } from '@/components/indicador-atualizacao';
import { StatusBadge } from '@/components/status-badge';
import { BadgePrioridade } from '@/components/badge-prioridade';
import { BadgeOrigem } from '@/components/badge-origem';
import { Botao } from '@/components/ui/button';
import { Esqueleto } from '@/components/ui/skeleton';
import {
  Cartao,
  CartaoCabecalho,
  CartaoConteudo,
  CartaoDescricao,
  CartaoTitulo,
} from '@/components/ui/card';

/** Tom do cartão de cada status, alinhado ao badge correspondente. */
const TONS: Record<StatusSolicitacao, string> = {
  ENVIADO: 'bg-status-enviado-fundo text-status-enviado-texto',
  LIDO_PELA_REGULACAO: 'bg-status-lido-fundo text-status-lido-texto',
  PENDENCIA_DOCUMENTACAO: 'bg-status-pendencia-fundo text-status-pendencia-texto',
  EM_PROCESSO: 'bg-status-processo-fundo text-status-processo-texto',
  AGENDADO: 'bg-status-agendado-fundo text-status-agendado-texto',
  DEVOLVIDO: 'bg-status-devolvido-fundo text-status-devolvido-texto',
  CANCELADO: 'bg-status-cancelado-fundo text-status-cancelado-texto',
};

export function PaginaPainel() {
  const { usuario } = useAuth();

  const { data, isPending, isFetching } = useQuery({
    queryKey: ['estatisticas'],
    queryFn: () => api.get<Estatisticas>('/solicitacoes/estatisticas'),
    refetchInterval: INTERVALO_ATUALIZACAO_MS,
  });

  if (!usuario) return null;

  const ehUbs = usuario.perfil === 'UBS';
  const ehRegulacao = usuario.perfil === 'REGULACAO';
  const ehAdmin = usuario.perfil === 'ADMINISTRADOR';

  // O administrador não opera solicitações; seu painel é o de cadastros.
  if (ehAdmin) {
    return <PainelAdministrador nome={usuario.nome} />;
  }

  const maiorEspecialidade = data?.porEspecialidade[0]?.total ?? 1;

  return (
    <>
      <CabecalhoPagina
        titulo={`Olá, ${usuario.nome.split(' ')[0]}`}
        descricao={
          ehUbs
            ? 'Situação das solicitações registradas pela sua unidade.'
            : 'Situação das solicitações de todas as unidades.'
        }
        acoes={
          <div className="flex items-center gap-3">
            <IndicadorAtualizacao buscando={isFetching} />
            {podeRegistrarSolicitacao(usuario.perfil) && (
              <Botao asChild>
                <Link to="/solicitacoes/nova">
                  <Plus aria-hidden="true" />
                  Nova solicitação
                </Link>
              </Botao>
            )}
          </div>
        }
      />

      {isPending ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          {STATUS.map((s) => (
            <Esqueleto key={s} className="h-20" />
          ))}
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          {/* Primeiro cartão da fileira, e só para a Regulação: é o único que
              pede uma ação agora. Documento anexado não muda status, então
              não aparece em nenhum dos cartões seguintes — sem este, some no
              volume. */}
          {ehRegulacao && (
            <CartaoIndicador
              rotulo="Documentação anexada"
              valor={data?.documentacaoAnexada ?? 0}
              icone={Paperclip}
              tom="bg-status-pendencia-fundo text-status-pendencia-texto"
              para="/solicitacoes?documentoNovo=1"
              descricao="Aguardando sua reavaliação"
            />
          )}

          {/* Ao lado do anterior, e pela mesma razão: corrigir não muda status,
              então um pedido cuja especialidade trocou depois da leitura
              continuaria sendo analisado pelos dados antigos. */}
          {ehRegulacao && (
            <CartaoIndicador
              rotulo="Dados corrigidos"
              valor={data?.edicaoNaoVista ?? 0}
              icone={PencilLine}
              tom="bg-status-lido-fundo text-status-lido-texto"
              para="/solicitacoes?editada=1"
              descricao="Aguardando sua reavaliação"
            />
          )}

          {/* Pedido sem decisão é vaga parada: enquanto ninguém confirma, o
              horário segue reservado para quem já avisou que não vem. Fora do
              painel da UBS, que não decide — ela acompanha pela lista. */}
          {!ehUbs && (
            <CartaoIndicador
              rotulo="Cancelamento pedido"
              valor={data?.cancelamentoPedido ?? 0}
              icone={CircleSlash}
              tom="bg-status-cancelado-fundo text-status-cancelado-texto"
              para="/solicitacoes?cancelamento=1"
              descricao="Aguardando decisão"
            />
          )}

          {/* O estoque anterior ao KAVI que ainda espera. Para todos os
              perfis, cada um no próprio alcance: a UBS vê as da unidade dela,
              o gestor as das unidades que acompanha, e a Regulação o município
              inteiro — o mesmo recorte que o resto do painel já usa. Só as em
              aberto: agendada ou encerrada não pede nada de ninguém. */}
          <CartaoIndicador
            rotulo="Demanda reprimida"
            valor={data?.demandaReprimida ?? 0}
            icone={History}
            tom="bg-silenciado text-silenciado-texto"
            para={`/demanda-reprimida?status=${VALOR_STATUS_ABERTOS}`}
            descricao="Anteriores ao KAVI, ainda na fila"
          />

          {STATUS.map((status) => {
            const definicao = DEFINICOES_STATUS[status];
            return (
              <CartaoIndicador
                key={status}
                rotulo={definicao.rotulo}
                valor={data?.porStatus[status] ?? 0}
                icone={definicao.icone}
                tom={TONS[status]}
                para={`/solicitacoes?status=${status}`}
              />
            );
          })}
        </div>
      )}

      <div className="mt-5 grid gap-5 lg:grid-cols-3">
        <Cartao className="lg:col-span-2">
          <CartaoCabecalho>
            <CartaoTitulo>Solicitações recentes</CartaoTitulo>
            <CartaoDescricao>As cinco últimas registradas.</CartaoDescricao>
          </CartaoCabecalho>
          <CartaoConteudo>
            {isPending && <Esqueleto className="h-40" />}

            {data && data.recentes.length === 0 && (
              <p className="text-silenciado-texto py-6 text-center text-sm">
                {ehUbs
                  ? 'Sua unidade ainda não registrou solicitações.'
                  : 'Nenhuma solicitação registrada até o momento.'}
              </p>
            )}

            <ul className="divide-borda divide-y">
              {data?.recentes.map((solicitacao) => (
                <li key={solicitacao.id} className="py-3 first:pt-0 last:pb-0">
                  <Link
                    to={`/solicitacoes/${solicitacao.id}`}
                    className="flex flex-wrap items-center gap-x-3 gap-y-1.5"
                  >
                    <span className="text-marca font-mono text-sm font-medium hover:underline">
                      {solicitacao.protocolo}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-sm">
                      {solicitacao.pacienteNome}
                    </span>
                    <BadgeOrigem origem={solicitacao.origem} tamanho="sm" />
                    <BadgePrioridade prioridade={solicitacao.prioridade} />
                    <StatusBadge status={solicitacao.status} tamanho="sm" />
                  </Link>
                  <p className="text-silenciado-texto mt-1 text-xs">
                    {ROTULOS_TIPO[solicitacao.tipo]} · {solicitacao.especialidade.nome} ·{' '}
                    {formatarData(solicitacao.criadoEm)}
                    {!ehUbs && ` · ${solicitacao.ubs.nome}`}
                  </p>
                </li>
              ))}
            </ul>

            {data && data.recentes.length > 0 && (
              <Botao variante="fantasma" tamanho="sm" asChild className="mt-3 -ml-3">
                <Link to="/solicitacoes">Ver todas as solicitações</Link>
              </Botao>
            )}
          </CartaoConteudo>
        </Cartao>

        <div className="flex flex-col gap-5">
          <Cartao>
            <CartaoCabecalho>
              <CartaoTitulo>Em aberto por prioridade</CartaoTitulo>
              <CartaoDescricao>Exclui as já agendadas.</CartaoDescricao>
            </CartaoCabecalho>
            <CartaoConteudo className="flex flex-col gap-2">
              {(['URGENTE', 'PRIORITARIA', 'ELETIVA'] as const).map((prioridade) => (
                <div key={prioridade} className="flex items-center justify-between gap-2">
                  <BadgePrioridade prioridade={prioridade} />
                  <span className="text-sm font-semibold tabular-nums">
                    {data?.porPrioridade[prioridade] ?? 0}
                  </span>
                </div>
              ))}
            </CartaoConteudo>
          </Cartao>

          <Cartao>
            <CartaoCabecalho>
              <CartaoTitulo>Especialidades mais solicitadas</CartaoTitulo>
              <CartaoDescricao>
                Últimos {data?.periodoDias ?? 30} dias · {data?.totalPeriodo ?? 0} solicitações.
              </CartaoDescricao>
            </CartaoCabecalho>
            <CartaoConteudo>
              {data && data.porEspecialidade.length === 0 && (
                <p className="text-silenciado-texto text-sm">Sem dados no período.</p>
              )}

              <ul className="flex flex-col gap-2.5">
                {data?.porEspecialidade.map((linha) => (
                  <li key={linha.especialidadeId}>
                    <div className="mb-1 flex items-center justify-between gap-2 text-sm">
                      <span className="truncate">{linha.nome}</span>
                      <span className="font-semibold tabular-nums">{linha.total}</span>
                    </div>
                    {/* Barra decorativa: o número ao lado já carrega o dado. */}
                    <div className="bg-silenciado h-1.5 overflow-hidden rounded-full">
                      <div
                        className="bg-marca h-full rounded-full"
                        style={{ width: `${(linha.total / maiorEspecialidade) * 100}%` }}
                        aria-hidden="true"
                      />
                    </div>
                  </li>
                ))}
              </ul>
            </CartaoConteudo>
          </Cartao>
        </div>
      </div>
    </>
  );
}

function PainelAdministrador({ nome }: { nome: string }) {
  return (
    <>
      <CabecalhoPagina
        titulo={`Olá, ${nome.split(' ')[0]}`}
        descricao={`Você está conectado com o perfil ${ROTULOS_PERFIL.ADMINISTRADOR}.`}
      />

      <Cartao>
        <CartaoCabecalho>
          <CartaoTitulo>Administração do sistema</CartaoTitulo>
          <CartaoDescricao>
            Use o menu lateral para gerenciar usuários, unidades e catálogos, e para consultar a
            trilha de auditoria.
          </CartaoDescricao>
        </CartaoCabecalho>
        <CartaoConteudo className="text-silenciado-texto flex flex-col gap-2 text-sm">
          <p>
            O perfil Administrador não registra nem analisa solicitações — essas ações pertencem à
            UBS e à Regulação. Para acompanhar o fluxo, consulte a auditoria.
          </p>
        </CartaoConteudo>
      </Cartao>
    </>
  );
}
